import { PretaxAccountV2Type } from 'constants/accounts';
import { PretaxWalletTransactionType } from 'constants/transactions';
import dayjs from 'dayjs';
import { gatewayClient } from 'utils/gatewayClient';

// Alegeus/CDH APIs
export interface FetchAccountsResponse {
  accounts: Array<{
    account_detail_info: {
      account_info: Array<{
        additional_deposits: number;
        balance: number;
        employee_relation: string;
        is_dependent: boolean;
        flex_account_key: number;
        first_name: string;
        last_name: string;
        status: string;
      }>;
      account_type: string;
      account_type_class_code: string;
      available_balance: number;
      balance_due: number;
      display_options: number;
      effective_date: string;
      primary_fund_rollover_rule: string;
      remaining_balance: number;
      spending_last_date: string;
      account_end_date?: string;
      account_payroll_info?: {
        contributions_ytd: number;
        employee_annual_election: number;
        employee_ppp_contribution: number;
        employer_ppp_contribution_amount: number;
        employer_annual_election: number;
        payroll_cycle: string;
        plan_start_date?: string;
        plan_end_date?: string;
      };
      submit_claims_last_date?: string;
    };
    account_display_header: string;
    account_status_code: string;
    account_type: string;
    account_type_class_code: string;
    annual_election: number;
    bank_routing_no: string;
    flex_account_id: string;
    grace_period_end_date: string;
    grace_period_end_date_formatted: string;
    hsa_routing_no?: string;
    is_card_eligible: boolean;
    plan_end_date: string;
    plan_end_date_formatted?: string;
    plan_start_date: string;
    plan_start_date_formatted?: string;
    total_hsa_balance: number;
  }>;
}

export function fetchAccounts() {
  return gatewayClient.get<FetchAccountsResponse>('/cdh/v2/accounts');
}

export interface FetchAccountDetailResponse {
  account_detail_info: {
    account_info: Array<{
      additional_deposits: number;
      balance: number;
      employee_relation: string;
      is_dependent: boolean;
      flex_account_key: number;
      first_name: string;
      last_name: string;
      status: string;
    }>;
    account_type: string;
    account_type_class_code: string;
    available_balance: number;
    balance_due: number;
    display_options: number;
    effective_date: string;
    primary_fund_rollover_rule: string;
    remaining_balance: number;
    spending_last_date: string;
    account_end_date?: string;
    account_payroll_info?: {
      contributions_ytd: number;
      employee_annual_election: number;
      employee_ppp_contribution: number;
      employer_ppp_contribution_amount: number;
      employer_annual_election: number;
      payroll_cycle: string;
      plan_start_date?: string;
      plan_end_date?: string;
    };
    submit_claims_last_date?: string;
  };
}

export function fetchAccountDetail({
  account_id,
  account_type,
}: {
  account_id: string;
  account_type: string;
}) {
  return gatewayClient.get<FetchAccountDetailResponse>(
    `/cdh/v2/accounts/${account_id}/types/${account_type}`
  );
}

export interface FetchHSASummaryResponse {
  summary: {
    current_year: {
      year: number;
      contributions: number;
      total_contributions: number;
      family_limit: number;
      single_limit: number;
    };
    year_to_date: {
      employee_contributions: number;
      employer_contributions: number;
      total_contributions: number;
    };
    prior_year: {
      employee_contributions: number;
      employer_contributions: number;
      total_contributions: number;
      family_limit: number;
      single_limit: number;
    };
    total_available_to_invest: number;
    total_available_to_spend: number;
    total_hsa_balance: number;
    current_hsa_balance: number;
    trust_account: {
      number: string;
      min_balance: number;
    };
    hsa_coverage_tier: string;
    hsa_account_number: string;
  };
}

export function fetchHSASummary({ account_id }: { account_id: string }) {
  return gatewayClient.get<FetchHSASummaryResponse>(
    `/cdh/v2/accounts/${account_id}/hsa-summary`
  );
}

export interface FetchHSAInvestmentsResponse {
  investments: {
    portfolio_balance: number;
    total_balance: number;
  };
}

export function fetchHSAInvestments({ account_id }: { account_id: string }) {
  return gatewayClient.get<FetchHSAInvestmentsResponse>(
    `/cdh/v2/accounts/${account_id}/hsa-investments`
  );
}

export interface FetchPretaxPlanResponse {
  pretax_plan: {
    alegeus_plan_id: string;
    alegeus_record_id: string;
    company_id: string;
    grace_period_date: string;
    grace_period_date_formatted: string;
    plan_end_date: string;
    plan_end_date_formatted: string;
    plan_start_date: string;
    plan_start_date_formatted: string;
    runout_period_date: string;
    runout_period_date_formatted: string;
    twic_plan_type: string;
  };
}

export function fetchPretaxPlan({ plan_type }: { plan_type: string }) {
  return gatewayClient.get(`/cdh/v2/accounts/pretax-plans/${plan_type}`);
}

// Pretax 2.0
export interface FetchPretaxWalletResponse {
  employeeAccount: {
    id: string;
    employeeId: string;
    accountStartDate: string;
    accountEndDate: string;
    gracePeriod: string;
    runoutPeriod: string;
    autoDeposit: {
      companyCalendarId: string;
      employeeAccountId: string;
      employeeExpectedFutureContribution: number;
      employeeLastAdjustment: number;
      employerExpectedFutureContribution: number;
      employerLastAdjustment: number;
      endDate: number;
      id: number;
      nextDepositDate: string;
      numberOfPayrolls: number;
    };
    companyPlan: {
      name: string;
      description: string;
      planType: string;
      companyId: string;
      endDate: string;
      gracePeriod: string;
      runoutPeriod: string;
      maximumBalanceToRollover: {
        type: string;
        cap?: number;
      };
      maximumElectionAmount: number;
      posttaxEnabled?: boolean;
      memberEnrollmentSettings?: null | {
        frequency: string;
        cutoffDayOfMonth: number;
      };
    };
    electionDetails?: {
      employeeAnnualElection?: number;
      employeePayPeriodElection?: number;
      employeePosttaxPayPeriodElection?: number;
      employerAnnualElection?: number;
      employerPayPeriodElection?: number;
      employerPosttaxPayPeriodElection?: number;
    };
    currentBalance: number;
    balanceDue: number;
    selectedCoverageTier: string;
    spent: number;
    contributionDetailsYTD: {
      employeeContributionsYTD: number;
      employerContributionsYTD: number;
      employeePosttaxContributionsYTD?: number;
      employerPosttaxContributionsYTD?: number;
    };
    totalRolloverContributions: {
      totalEmployeeRolloverContribution: number;
      totalEmployerRolloverContribution: number;
    };
  };
}

export const PRETAX_ACCOUNTS_ENDPOINT = '/pretax/accounts/employee-accounts';

export function fetchPretaxWalletDetails({ wallet_id }: { wallet_id: string }) {
  return gatewayClient.get(`${PRETAX_ACCOUNTS_ENDPOINT}/${wallet_id}`);
}

export interface FetchPretaxWalletTransactionsResponse {
  data: [
    {
      id: string;
      amount: number;
      created: string;
      description: string;
      status: string;
      type: string;
      referenceId: string;
    }
  ];
  count: number;
  page: number;
}

export function fetchPretaxWalletTransactions({
  wallet_id,
  page,
  size,
  type = PretaxWalletTransactionType.ALL,
  startDate,
  endDate,
}: {
  wallet_id: string;
  page: number;
  size: number;
  type?: PretaxWalletTransactionType;
  startDate?: string;
  endDate?: string;
}) {
  return gatewayClient.get<FetchPretaxWalletTransactionsResponse>(
    `${PRETAX_ACCOUNTS_ENDPOINT}/${wallet_id}/transactions`,
    {
      params: {
        page,
        size,
        types: type !== PretaxWalletTransactionType.ALL ? type : undefined,
        startDate: startDate
          ? dayjs(startDate).format('YYYY-MM-DD')
          : undefined,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
      },
    }
  );
}

export interface FetchPretaxEligibleWalletsResponse {
  eligibleAccounts: Array<{
    planId: string;
    planType: PretaxAccountV2Type;
    planName: string;
    planStartDate: string;
  }>;
}
export function fetchPretaxEligibleWallets({
  formaEnrollment,
}: {
  formaEnrollment?: boolean;
}) {
  return gatewayClient.get<FetchPretaxEligibleWalletsResponse>(
    `/pretax/accounts/eligible-accounts`,
    { params: { formaEnrollment } }
  );
}

export interface FetchPretaxWalletPlanResponse {
  plan: {
    id: string;
    planType: PretaxAccountV2Type;
    companyId: string;
    startDate: string;
    endDate: string;
    name: string;
    description: string;
    planStatus: string;
    eligibleEnrollees: [];
    allowCardPayment: boolean;
    allowRollover: boolean;
    gracePeriodDays: number;
    lumpSum: unknown | null;
    maximumBalanceToRollover: unknown | null;
    maximumElectionAmount: number;
    minimumBalanceToRollover: unknown | null;
    minimumElectionAmount: number;
    postTerminationEndOfMonthGracePeriod: boolean;
    postTerminationRunoutDays: number;
    runoutDays: number;
    defaultCoverageTier: string | null;
    prefunded: boolean;
    gracePeriod: string;
    runoutPeriod: string;
    rolloverDate: string | null;
    allowAddDependentsToAccount: boolean;
    fundingMethod: string;
    autoDepositCalendar: null;
    posttaxEnabled: boolean;
    maximumPosttaxElectionAmount: number | undefined;
    cardPaymentCopayAmounts: [];
    cardPaymentReceiptSettings: string;
    memberEnrollmentSettings: {
      frequency: string;
      cutoffDayOfMonth: number;
    } | null;
  };
}
export function fetchPretaxWalletPlan({ planId }: { planId: string }) {
  return gatewayClient.get<FetchPretaxWalletPlanResponse>(
    `/pretax/accounts/company-plans/${planId}`
  );
}
